import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  SearchBox,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip
} from '@fluentui/react-components';
import {
  AddRegular,
  AlertRegular,
  ArrowSortRegular,
  ArrowSyncRegular,
  CheckmarkCircle20Filled,
  DismissRegular,
  ErrorCircleFilled,
  WarningFilled
} from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiPermissionBanner } from '../components/ApiPermissionBanner';
import { RankManagementDialog } from '../components/RankManagementDialog';
import {
  EnrollmentStatusLoadState,
  useDisclaimerEnrollmentSummary,
} from '../contexts/DisclaimerEnrollmentSummaryContext';
import { useError } from '../contexts/ErrorContext';
import { useUnifiedApp } from '../contexts/UnifiedAppContext';
import { managedPolicy, PageNameEvent } from '../hooks/eventTracker.types';
import { triggerAdminConsent } from '../hooks/useAdminConsent';
import { useAuthGuard } from '../hooks/useAuthGuard';
import useEventTracker from '../hooks/useEventTracker';
import { useGeneralConfig } from '../hooks/useGeneralConfig';
import { useGeneralPermissions } from '../hooks/useGeneralPermissions';
import { apiClient } from '../services/apiClient';
import { Policy, policyService } from '../services/policyService';
import { ElementButton, PageName } from '../tracking/tracking.types';
import type { CustomDisclaimer } from '../types/customDisclaimers';
import { findAssignedDisclaimer } from '../utils/customDisclaimerApiUtils';
import { formatLastSync } from '../utils/dateFormatters';
import { useStyles } from './Policies.style';

export interface IRankableAssignment {
  groupId: string;
  groupName: string;
  policyId: string;
  policyName: string;
  rank: number;
  domainCount: number;
  isAdmManaged: boolean;
}

// Helper function to determine policy federation status
const getPolicyFederationStatus = (policy: Policy, managedPolicies: Policy[] = []): {status: string;isReady: boolean;} => {
  // First check if a managed version of this policy exists (e.g., "US Staff" -> "US Staff_OD")
  const managedVersionExists = managedPolicies.some((mp) =>
  mp.name === `${policy.name}_OD`
  );

  if (managedVersionExists) {
    return { status: 'Onboarded', isReady: true };
  }

  return {
    status: policy.readyForOnboarding ? 'Ready to onboard' : 'Federation not configured',
    isReady: policy.readyForOnboarding ?? false
  };
};

interface PolicyData extends Policy {
  isTeamsPolicy?: boolean;
  rank?: number;
}

// Helper function to render assignment summary with tooltip
const renderAssignmentSummary = (policy: PolicyData, styles: any) => {
  const assignments = (policy as any)?.assignments;

  if (!assignments) {
    return <span data-testid="policies-assignment-empty-span">Not Assigned</span>;
  }

  const groups = assignments.groups || [];
  const users = assignments.users || [];
  const totalAssignments = assignments.totalAssignments || 0;

  // Create summary text for display
  const summaryItems: string[] = [];
  if (groups.length > 0) {
    summaryItems.push(`${groups.length} group${groups.length === 1 ? '' : 's'}`);
  }
  if (users.length > 0) {
    summaryItems.push(`${users.length} user${users.length === 1 ? '' : 's'}`);
  }

  const summaryText = summaryItems.length > 0 ? summaryItems.join(', ') : 'Not Assigned';

  // If no assignments, return simple text
  if (totalAssignments === 0) {
    return <span data-testid="policies-assignment-empty-total-span">Not Assigned</span>;
  }

  // Create tooltip content
  const tooltipContent =
  <div className={styles.tooltipContent} data-testid="policies-tooltip-content-div">
      {groups.length > 0 &&
    <div className={styles.tooltipSection} data-testid="policies-tooltip-section-div">
          <div className={styles.tooltipTitle} data-testid="policies-tooltip-title-div">Groups ({groups.length})</div>
          <ul className={styles.tooltipList} data-testid="policies-tooltip-list-ul">
            {groups.map((group: any) =>
        <li key={group.groupId || group.displayName} data-testid="policies-tooltip-group-item-li">{group.displayName || group.groupId}</li>
        )}
          </ul>
        </div>
    }
      {users.length > 0 &&
    <div className={styles.tooltipSection} data-testid="policies-tooltip-section-2-div">
          <div className={styles.tooltipTitle} data-testid="policies-tooltip-title-2-div">Users ({users.length})</div>
          <ul className={styles.tooltipList} data-testid="policies-tooltip-list-2-ul">
            {users.map((user: any) =>
        <li key={user.userId || user.userEmail || user.displayName} data-testid="policies-tooltip-user-item-li">{user.displayName || user.userEmail}</li>
        )}
          </ul>
        </div>
    }
    </div>;


  // Return tooltip wrapped content
  return (
    <Tooltip content={tooltipContent} relationship="description" data-testid="policies-tooltip">
      <div className={styles.assignmentSummary} style={{ cursor: 'help' }} data-testid="policies-assignment-summary-div">
        <span data-testid="policies-assignment-summary-text-span">{summaryText}</span>
        <span className={styles.assignmentItem} data-testid="policies-assignment-item-span">
          {totalAssignments} assignment{totalAssignments === 1 ? '' : 's'} total
        </span>
      </div>
    </Tooltip>);

};

// Resolves the disclaimer (if any) assigned to a policy by trying its id first,
// then falling back to its name — both normalized for case/whitespace by
// findAssignedDisclaimer — against the shared disclaimer collection's
// policyIds and policies[].id/name.
const resolveAssignedDisclaimer = (
  policy: PolicyData,
  disclaimers: readonly CustomDisclaimer[],
): CustomDisclaimer | undefined =>
  findAssignedDisclaimer(disclaimers, policy.id) ?? findAssignedDisclaimer(disclaimers, policy.name);

// Renders the Managed by ADM Disclaimer cell. Only ever renders "Unassigned"
// once the shared disclaimer collection has actually loaded (disclaimerState
// === 'ready'); loading/error states show the page's established loading/error
// affordances instead so an unresolved read is never misreported as Unassigned.
const renderDisclaimerCell = (
  policy: PolicyData,
  disclaimers: readonly CustomDisclaimer[],
  disclaimerState: EnrollmentStatusLoadState,
  styles: any
) => {
  if (disclaimerState === 'loading') {
    return <Spinner size="tiny" label="Loading disclaimer" data-testid="policies-disclaimer-loading-spinner" />;
  }

  if (disclaimerState === 'error') {
    return <span data-testid="policies-disclaimer-unresolved-span">—</span>;
  }

  const assignedDisclaimer = resolveAssignedDisclaimer(policy, disclaimers);
  if (!assignedDisclaimer) {
    return <span data-testid="policies-disclaimer-unassigned-span">Unassigned</span>;
  }

  const disclaimerHref = `/custom-disclaimers/${assignedDisclaimer.id}`;
  return (
    <Link className={styles.disclaimerLink} to={disclaimerHref} data-testid="policies-disclaimer-link">
      {assignedDisclaimer.name}
    </Link>
  );
};

// Pure helper — no state/context dependency, safe at module level
const getUserGroupDisplayNames = (policy: any): string => {
  if (policy.assignments?.groups && policy.assignments.groups.length > 0) {
    const displayNames: string[] = [];
    for (const group of policy.assignments.groups) {
      if (group.displayName) {
        displayNames.push(group.displayName);
      }
    }
    return displayNames.join(', ');
  }
  return 'Not Assigned';
};

const transformTeamsPolicy = (policy: any): PolicyData => {
  const policyName = policy.name || policy.Identity || policy.displayName || policy.identity;
  return {
    id: policy.id || policy.Identity || policy.identity,
    name: policyName,
    status: 'Active' as const,
    precedenceRank: policy.rank || 0,
    managementMode: policyName.endsWith('_OD') ? 'ADM Managed' as const : 'Teams Managed' as const,
    syncWithFeed: false,
    userGroup: getUserGroupDisplayNames(policy),
    bilateralChat: true,
    includedDomains: Array.isArray(policy.allowedDomains) ? policy.allowedDomains : [],
    customDomains: [],
    excludedDomains: Array.isArray(policy.blockedDomains) ? policy.blockedDomains : [],
    associatedTeamsPolicies: [policy.identity],
    lastSyncWithTeams: policy.syncedAt || new Date().toISOString(),
    lastFeedSync: new Date().toISOString(),
    syncStatus: policy.driftStatus === 'drift_detected' ? 'Drift Detected' : 'In Sync' as const,
    createdAt: policy.createdDate || new Date().toISOString(),
    updatedAt: policy.lastModified || new Date().toISOString(),
    createdBy: 'Teams PowerShell',
    isTeamsPolicy: true,
    driftStatus: policy.driftStatus,
    rank: policy.rank,
    // CRITICAL: Preserve both federation data formats
    teamsPolicyDetails: policy.teamsPolicyDetails,
    federationConfig: policy.federationConfig,
    readyForOnboarding: policy.readyForOnboarding,
    // CRITICAL: Preserve assignments data for multiple assignment display
    assignments: policy.assignments,
    // CRITICAL: Preserve deletion status fields
    deletionStatus: policy.deletionStatus,
    deletionRequestedBy: policy.deletionRequestedBy,
    deletionRequestedAt: policy.deletionRequestedAt
  } as PolicyData;
};

const transformNonTeamsPolicy = (policy: any): PolicyData => {
  return {
    ...policy,
    name: policy.Identity || policy.name || policy.id || 'Unknown Policy',
    includedDomains: Array.isArray(policy.domains) ? policy.domains : [],
    isTeamsPolicy: false,
    managementMode: policy.managementMode || 'Fully Delegated',
    // Preserve both federation data formats if they exist
    teamsPolicyDetails: policy.teamsPolicyDetails,
    federationConfig: policy.federationConfig,
    readyForOnboarding: policy.readyForOnboarding,
    // Preserve assignments data
    assignments: policy.assignments,
    // CRITICAL: Preserve deletion status fields
    deletionStatus: policy.deletionStatus,
    deletionRequestedBy: policy.deletionRequestedBy,
    deletionRequestedAt: policy.deletionRequestedAt
  } as PolicyData;
};

const transformPolicies = (policies: any[]): PolicyData[] => {
  return policies.map((policy: any) => {
    if (policy.type === 'teams') {
      return transformTeamsPolicy(policy);
    } else {
      return transformNonTeamsPolicy(policy);
    }
  });
};

export const Policies: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { isReady, isLoading: authLoading, user } = useAuthGuard(); // NEW: Use auth guard
  const { showError, showWarning, showInfo } = useError();
  const appContext = useUnifiedApp();
  const [loading, setLoading] = useState(true);
  const [managedPolicies, setManagedPolicies] = useState<PolicyData[]>([]);
  const [unmanagedPolicies, setUnmanagedPolicies] = useState<PolicyData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rankDialogOpen, setRankDialogOpen] = useState(false);
  const [savingRanks, setSavingRanks] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastPolicySync, setLastPolicySync] = useState<Date | null>(null);
  const [lastAssignmentSync, setLastAssignmentSync] = useState<Date | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { permissions, loadingPermissions, loadPermissions } = useGeneralPermissions();
  const { config, loadingConfig, loadConfig } = useGeneralConfig();
  const { disclaimers, disclaimerState } = useDisclaimerEnrollmentSummary();
  const [warningBannerDismissed, setWarningBannerDismissed] = useState(false);
  const [showSyncSuccessBanner, setShowSyncSuccessBanner] = useState(false);
  const [syncStats, setSyncStats] = useState<any>(null);
  const [showAssignmentSyncError, setShowAssignmentSyncError] = useState(false);
  const [createdPolicy, setCreatedPolicy] = useState<string | null>(null);


  const eventTracker = useEventTracker();
  const handleActionOnLogin = async () => {
    await eventTracker.eventTrackOnLogin();
  };
  const handleActionOnFirstLaunch = async () => {
    await eventTracker.eventTrackOnAppLaunched({ pageName: PageName.POLICIES });
  };

  const handleActionOnAppLaunched = async (admManagePolicy: managedPolicy[], tacManagePolicy: managedPolicy[]) => {
    const tracker = {
      pageName: PageName.POLICIES,
      ...(admManagePolicy && { managedByAdm: admManagePolicy }),
      ...(tacManagePolicy && { managedByTac: tacManagePolicy })
    };
    await eventTracker.eventTrackOnAppLaunched(tracker as PageNameEvent);
  };

  
  const loadPolicies = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      // Load all policies from unified endpoint
      const policiesWithSyncHistory = await policyService.getPoliciesWithSyncHistory();
      if (!silent) {
        console.log('Loaded policies:', policiesWithSyncHistory);
      }

      // handle the case where syncHistory might be missing or have unexpected structure, we add checks before accessing nested properties
      // to support backwards compatibility with older backend versions that don't have sync history
      const lastPolicySyncDate = policiesWithSyncHistory.syncHistory?.policy?.created_at ? new Date(policiesWithSyncHistory.syncHistory.policy.created_at) : null;
      const lastAssignmentSyncDate = policiesWithSyncHistory.syncHistory?.assignment?.created_at ? new Date(policiesWithSyncHistory.syncHistory.assignment.created_at) : null;
      setLastPolicySync(lastPolicySyncDate);
      setLastAssignmentSync(lastAssignmentSyncDate);

      // Check teamsPolicyDetails for unmanaged policies
      if (!silent) {
        policiesWithSyncHistory.data.policies.forEach((policy) => {
          if (!policy.name?.endsWith('_OD')) {
            console.log(`Policy: ${policy.name}`, {
              hasTeamsPolicyDetails: !!policy.teamsPolicyDetails,
              teamsPolicyDetails: policy.teamsPolicyDetails
            });
          }
        });
      }

      // Transform and categorize policies
      const transformedPolicies = transformPolicies(policiesWithSyncHistory.data.policies);

      // Separate managed and unmanaged policies
      // Policies with _OD suffix are managed by ADM
      const managed = transformedPolicies.filter((p) =>
      p.name && p.name.endsWith('_OD')
      );
      const unmanaged = transformedPolicies.filter((p) =>
      p.name && !p.name.endsWith('_OD')
      );

      setManagedPolicies(managed);
      setUnmanagedPolicies(unmanaged);
    } catch (err: any) {
      console.error('Failed to load policies:', err);
      const errorMessage = err?.message || 'Failed to load policies';
      setError(errorMessage);

      // Show error modal with retry option
      showError({
        message: errorMessage,
        title: 'Failed to Load Policies',
        severity: 'error',
        details: err?.stack || err?.toString(),
        onRetry: () => {
          setError(null);
          loadPolicies();
        }
      });
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [showError]);

  const handleActionOnClick = async (elementButton: ElementButton) => {
    await eventTracker.eventTrackOnElementClicked({
      elementType: "button",
      elementName: elementButton,
      pageName: PageName.POLICIES
    });
  };
  // CRITICAL FIX: Only load policies when authentication is ready
  useEffect(() => {
    if (isReady) {
      loadPermissions();
      loadConfig();
      loadPolicies();

      // Check if message bar was dismissed in this session
      const dismissed = sessionStorage.getItem('policies_warning_banner_dismissed') === 'true';
      setWarningBannerDismissed(dismissed);

      // first time login
      if (sessionStorage.getItem('msal_first_time_login') === 'true') {
        handleActionOnLogin();
        handleActionOnFirstLaunch();
        sessionStorage.setItem('msal_first_time_login', 'false');
        // Clear message bar dismissal on new login
        sessionStorage.removeItem('policies_warning_banner_dismissed');
        setWarningBannerDismissed(false);
      }
    }
  }, [isReady, loadPermissions, loadConfig, loadPolicies]);

  useEffect(() => {
    if (managedPolicies.length || unmanagedPolicies.length) {
      const mappedAdmManagePolicy = managedPolicies.map((item) => {
        const status = item.name.endsWith("_OD") ? "Onboarded" : item.readyForOnboarding ? "Ready to onboard" : "Federation not configured";
        return {
          policyId: item.id,
          member: item.assignments?.groups?.length ?? 0,
          status
        };
      });

      const mappedTacManagePolicy = unmanagedPolicies.map((item) => {
        const status = item.name.endsWith("_OD") ? "onboarded" : item.readyForOnboarding ? "ready to onboard" : "federation not configured";
        return {
          policyId: item.id,
          member: item.assignments?.groups?.length ?? 0,
          status
        };
      });
      handleActionOnAppLaunched(mappedAdmManagePolicy, mappedTacManagePolicy);
    }
  }, [managedPolicies, unmanagedPolicies]);

  // Listen for navigation state to trigger refresh after a policy change.
  useEffect(() => {
    const state = location.state as { refresh?: boolean; createdPolicy?: string; deletedPolicy?: string } | null;
    if (state?.refresh && isReady) {
      if (state.createdPolicy) {
        setCreatedPolicy(state.createdPolicy);
      }
      loadPolicies();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, isReady]);

  // Memoize deletion status to avoid re-running polling effect unnecessarily
  const hasDeletingPolicies = useMemo(() => {
    return [...managedPolicies, ...unmanagedPolicies].some(
      (p: any) => p.deletionStatus === 'pending' || p.deletionStatus === 'in_progress'
    );
  }, [managedPolicies, unmanagedPolicies]);

  // Poll for policy deletion completion - check every 5 seconds if there are any deleting policies
  useEffect(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (!hasDeletingPolicies) return;

    pollIntervalRef.current = setInterval(async () => {
      try {
        // Reload policies SILENTLY to check updated deletion statuses
        await loadPolicies(true); // true = silent mode, no loading spinner
        // Also refresh the app context to update sidebar counts
        if (appContext?.refreshData) {
          appContext.refreshData(true); // true = silent refresh (don't show loading spinner)
        }
      } catch (error) {
        console.error('Error polling for deletion:', error);
      }
    }, 15000); // Poll every 15 seconds (balanced for UX and server load)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasDeletingPolicies]);

  // Remove the old formatDate function - now using the utility

  const handleSyncTeams = async () => {
    try {
      setSyncing(true);

      // Call the sync API - the backend returns the sync result directly
      // But apiClient wraps it in { success: boolean, data: T }
      const response = await apiClient.post<{
        success: boolean;
        dryRun?: boolean;
        stats?: {
          synced: number;
          created: number;
          updated: number;
          total: number;
          skipped: number;
          errors: number;
          preserved?: number;
        };
        wouldDestroyData?: boolean;
        summary?: {
          total: number;
          synced: number;
          preserved: number;
          skipped: number;
          errors: number;
        };
        timestamp?: string;
      }>('/policies/sync', {});


      // The apiClient returns ApiResponse<T> but the backend returns the result directly
      // We need to handle both the wrapper success and the actual sync success
      // TypeScript type shows response as ApiResponse, but at runtime it's the direct result
      const result = response as any; // Runtime type differs from compile-time type

      // Check if the sync was successful
      if (result && result.success) {
        // Store sync stats and show success banner
        setSyncStats(result);
        setShowSyncSuccessBanner(true);
        
        // Hide the warning banner after successful sync
        setWarningBannerDismissed(true);
        sessionStorage.setItem('policies_warning_banner_dismissed', 'true');
        
        // Show assignment sync error banner if assignments were not included
        if (result.includeAssignments === false) {
          setShowAssignmentSyncError(true);
        } else {
          setShowAssignmentSyncError(false);
        }
      } else {
        // If result.success is false or result is missing
        const errorMessage = result?.success === false ?
        'Teams sync failed. Check backend logs for details.' :
        'Failed to sync Teams policies. The server returned an invalid response.';
        showError(errorMessage);
      }

      await loadPolicies();
    } catch (error) {
      console.error('Error syncing Teams policies:', error);
      showError('Failed to sync Teams policies. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddNewPolicy = () => {
    navigate('/wizard');
  };

  const handleManageRank = async () => {
    setRankDialogOpen(true);
  };

  const handleSaveRanks = async (updatedPolicies: Array<{originalRank: number;} & IRankableAssignment>) => {
    try {
      // Prepare the updates for the backend
      const updates = updatedPolicies.map((policy) => ({
        policyId: policy.policyId,
        policyName: policy.policyName,
        groupId: policy.groupId,
        newRank: policy.rank,
        oldRank: policy.originalRank || policy.rank // Use original rank for accurate audit logging
      }));

      // DEBUG: Log what we're sending
      console.log('🚀 Sending to backend:', JSON.stringify(updates, null, 2));

      // Call the bulk update endpoint
      const response = await apiClient.post('/assignment/bulk-update-ranks', { updates });

      // DEBUG: Log the actual response
      console.log('🔍 Backend response:', JSON.stringify(response, null, 2));

      // Reload policies regardless of partial success/failure
      await loadPolicies();

      // Determine notification type based on results (fixed TypeScript errors)
      const responseData = response.data as any;
      const hasSuccesses = responseData?.results && responseData.results.length > 0;
      const hasErrors = responseData?.errors && responseData.errors.length > 0;

      if (response.success) {
        // All succeeded
        console.log('✅ SUCCESS: Calling showInfo with message');
        showInfo('Policy ranks updated successfully', 'Success');
      } else if (hasSuccesses && hasErrors) {
        // Partial success - show warning instead of error
        showWarning(response.message || 'Some policy ranks were updated, but some failed');
      } else if (hasErrors && !hasSuccesses) {
        // Complete failure - show error
        console.error('❌ All policy rank updates failed:', response);
        showError(response.message || 'Failed to update policy ranks');
      } else {
        // Unknown state - show warning
        showWarning(response.message || 'Policy rank update completed with unknown status');
      }
    } catch (error: any) {
      showError(error.message || 'An error occurred while updating policy ranks');
    }
  };

  const filteredManagedPolicies = managedPolicies.filter((policy) =>
  policy.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUnmanagedPolicies = unmanagedPolicies.filter((policy) =>
  policy.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Shared permission logic - computed once and reused for both button and banner
  const permissionState = useMemo(() => {
    const appManageAssignmentPermission = permissions?.applications?.backend?.azure?.permissions?.assignment?.read ?? false;
    const userManageAssignmentPermission = user?.permissions?.assignment?.read ?? false;
    const hasManageAssignmentPermission = appManageAssignmentPermission || userManageAssignmentPermission;

    return {
      appManageAssignmentPermission,
      userManageAssignmentPermission,
      hasManageAssignmentPermission
    };
  }, [permissions, user]);

  // API permission state - checks if required API permissions are granted
  const apiPermissionState = useMemo(() => {
    const apiPermissions = config?.applications?.backend?.azure?.apiPermissions ?? [];
    const lowerPermissions = apiPermissions.map((p) => p.toLowerCase());

    const requiredPermissions = ['user_impersonation', 'profile', 'organization.read.all'];
    const missingPermissions = requiredPermissions.filter(
      (perm) => !lowerPermissions.includes(perm)
    );
    const hasMissingUserImpersonation = missingPermissions.includes('user_impersonation');
    const hasMissingProfile = missingPermissions.includes('profile');

    return {
      hasMissingApiPermissions: missingPermissions.length > 0,
      hasMissingUserImpersonation,
      hasMissingProfile,
      missingPermissions
    };
  }, [config]);

  const handleDismissWarningBanner = () => {
    setWarningBannerDismissed(true);
    sessionStorage.setItem('policies_warning_banner_dismissed', 'true');
  };

  // Determine if and what message to show
  const warningBanner = useMemo(() => {
    if (warningBannerDismissed || !permissions) return null;

    const { appManageAssignmentPermission, userManageAssignmentPermission } = permissionState;

    const textStyle = { color: '#806500' };

    // Case 1: App doesn't have permission but user does
    if (!appManageAssignmentPermission && userManageAssignmentPermission) {
      return (
        <span style={textStyle}>
          <Body1Strong>Policy assignments may be out of date.</Body1Strong> Click <Body1Strong>Sync Policies</Body1Strong> to update the list.
        </span>
      );
    }

    // Case 2: Neither app nor user has permission
    if (!appManageAssignmentPermission && !userManageAssignmentPermission) {
      return (
        <span style={textStyle}>
          <Body1Strong>Policy assignments may be out of date.</Body1Strong> To sync policies, ask your Entra admin to assign the Teams Reader role to the ADM app or to your user account in Entra ID.
        </span>
      );
    }

    return null;
  }, [warningBannerDismissed, permissions, permissionState]);

  const showApiPermissionBanner =
    (apiPermissionState.hasMissingUserImpersonation || apiPermissionState.hasMissingProfile) &&
    config !== null &&
    !loadingConfig;

  // ENHANCED LOADING STATE: Show loading while auth or data is loading
  if (authLoading || !isReady || loading) {
    return (
      <div className={styles.loadingContainer} data-testid="policies-loading-container-div">
        <Spinner size="large" label={authLoading ? "Authenticating..." : "Loading policies..."} data-testid="policies-loading-spinner" />
      </div>);

  }

  if (error) {
    return (
      <div className={styles.emptyState} data-testid="policies-empty-state-div">
        <Text data-testid="policies-error-loading-policies-text">Error loading policies: {error}</Text>
      </div>);

  }

  return (
    <div style={{ display: 'flex', rowGap: '24px', flexDirection: 'column' }} data-testid="policies-root-div">
      {(createdPolicy || warningBanner || (showSyncSuccessBanner && syncStats) || showAssignmentSyncError || showApiPermissionBanner) && (
        <div style={{ display: 'flex', rowGap: '16px', flexDirection: 'column' }} data-testid="policies-banner-div">
          {createdPolicy && (
            <MessageBar
              className={styles.createdPolicyMessageBar}
              icon={<CheckmarkCircle20Filled className={styles.createdPolicyIcon} />}
              intent="success"
              data-testid="policies-created-success-messagebar"
            >
              <MessageBarBody className={styles.createdPolicyMessageBody}>
                <span className={styles.createdPolicyMessageText}>
                  <Body1Strong>Policy ({createdPolicy}) created.</Body1Strong>{' '}
                  Assign a disclaimer for it to appear in Microsoft Teams.{' '}
                  <a
                    className={styles.createdPolicyLink}
                    href="/custom-disclaimers"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate('/custom-disclaimers');
                    }}
                  >
                    Manage disclaimers
                  </a>
                </span>
              </MessageBarBody>
              <MessageBarActions
                containerAction={
                  <Button
                    onClick={() => setCreatedPolicy(null)}
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Dismiss"
                    style={{ color: '#237B4B' }}
                    data-testid="policies-dismiss-created-success-button"
                  />
                }
              />
            </MessageBar>
          )}
          {/* API Permission Error Banner */}
          {showApiPermissionBanner && (
            <ApiPermissionBanner
              message="Unable to sync policy assignments or manage rank due to lack of permissions."
              sessionStorageKey="policies_api_permission_banner_dismissed"
              testIdPrefix="policies"
              onGrantAccess={() => {
                if (config?.applications?.backend?.azure) {
                  const { clientId, tenantId, displayName } = config.applications.backend.azure;
                  triggerAdminConsent(clientId, tenantId, displayName, ['user_impersonation', 'profile']);
                }
              }}
            />
          )}

          {/* Warning MessageBar */}
          {warningBanner && (
            <MessageBar icon={<span style={{color: "#806500" }}><WarningFilled/></span>} intent="warning" style={{backgroundColor: "#FFF2DB"}} data-testid="policies-permission-warning-messagebar">
              <MessageBarBody>
                {warningBanner}
              </MessageBarBody>
              <MessageBarActions
                containerAction={
                  <Button
                    onClick={handleDismissWarningBanner}
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Dismiss"
                    style={{ color: '#806500' }}
                    data-testid="policies-dismiss-messagebar-button"
                  />
                }
              />
            </MessageBar>
          )}

          {/* Sync Success Banner */}
          {showSyncSuccessBanner && syncStats && (
            <MessageBar intent="success" data-testid="policies-sync-success-messagebar">
              <MessageBarBody>
                <span style={{ fontSize: '14px', color: '#237B4B' }}>
                  <Body1Strong>Policies synced successfully. {syncStats.totalPoliciesSynced}</Body1Strong> policies synced from Teams ({syncStats.stats?.policies?.inserted || 0} created, {syncStats.stats?.policies?.updated || 0} updated).
                </span>
              </MessageBarBody>
              <MessageBarActions
                containerAction={
                  <Button
                    onClick={() => setShowSyncSuccessBanner(false)}
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Dismiss"
                    style={{ color: '#237B4B' }}
                    data-testid="policies-dismiss-sync-success-button"
                  />
                }
              />
            </MessageBar>
          )}

          {/* Assignment Sync Error Banner */}
          {showAssignmentSyncError && (
            <MessageBar icon={<ErrorCircleFilled />} intent="error" data-testid="policies-assignment-sync-error-messagebar">
              <MessageBarBody>
                <span style={{ fontSize: '14px', color: '#8A001E' }}>
                  <Body1Strong>Unable to sync policy assignments.</Body1Strong> The required admin role is missing. Ask your Entra admin to assign the Teams Reader role to the ADM app or your user account in Entra ID.
                </span>
              </MessageBarBody>
              <MessageBarActions
                containerAction={
                  <Button
                    onClick={() => setShowAssignmentSyncError(false)}
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="Dismiss"
                    style={{ color: '#8A001E' }}
                    data-testid="policies-dismiss-assignment-error-button"
                  />
                }
              />
            </MessageBar>
          )}
        </div>
      )}

      <div className={styles.container} data-testid="policies-container-div">
        {/* Header */}
        <div className={styles.header} data-testid="policies-header-div">
          <div className={styles.headerTop} data-testid="policies-header-top-div">
            <Text className={styles.title} data-testid="policies-policies-text">Policies</Text>
            <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
              <Text style={{  color: '#656565' }} data-testid="policies-last-sync-text">
                <Body1Strong>Policies last sync:</Body1Strong> {formatLastSync(lastPolicySync?.toISOString() ?? null)}
              </Text>
              <Text style={{  color: '#656565' }} data-testid="assignments-last-sync-text">
                <Body1Strong>Assignments last sync:</Body1Strong> {formatLastSync(lastAssignmentSync?.toISOString() ?? null)}
              </Text>
            </div>
          </div>
          <div className={styles.headerControls} data-testid="policies-header-controls-div">
            <Button
              appearance="primary"
              onClick={() => {
                handleSyncTeams();
                handleActionOnClick(ElementButton.SYNC_POLICY);
              }}
              icon={<ArrowSyncRegular data-testid="policies-arrow-sync-regular" />}
              disabled={syncing} data-testid="policies-sync-policies-button">

              {syncing ? 'Syncing...' : 'Sync Policies'}
            </Button>
            <Button
              appearance="secondary"
              onClick={() => {
                handleAddNewPolicy();
                handleActionOnClick(ElementButton.ADD_POLICY);
              }
              }
              icon={<AddRegular data-testid="policies-add-regular" />} data-testid="policies-add-policy-button">

              Add Policy
            </Button>
            {(() => {
              const { hasManageAssignmentPermission } = permissionState;
              const { hasMissingUserImpersonation, hasMissingProfile } = apiPermissionState;
              const isButtonLoading = loadingPermissions || loadingConfig || savingRanks;
              const isDisabled = isButtonLoading || !hasManageAssignmentPermission || hasMissingUserImpersonation || hasMissingProfile;

              const button = (
                <Button
                  appearance="secondary"
                  onClick={() => {
                    handleManageRank();
                    handleActionOnClick(ElementButton.MANAGE_RANK);
                  }}
                  icon={isButtonLoading ? <Spinner size="tiny" data-testid="policies-rank-saving-spinner" /> : <ArrowSortRegular data-testid="policies-arrow-sort-regular" />}
                  disabled={isDisabled}
                  data-testid="policies-manage-rank-button"
                >
                  {savingRanks ? 'Saving Ranks...' : 'Manage Rank'}
                </Button>
              );

              const tooltipMessage = hasMissingUserImpersonation || hasMissingProfile
                ? <div className={styles.manageRankTooltipContent}><Body1Strong>Manage Rank is unavailable.</Body1Strong><br /><Body1>Grant consent to enable it.</Body1></div>
                : <div className={styles.manageRankTooltipContent}><Body1Strong>Manage Rank is unavailable.</Body1Strong><br /><Body1>Please ask your Entra admin to assign the Teams Reader role to the ADM app or to your user account in Entra ID.</Body1></div>;

              return (!hasManageAssignmentPermission || hasMissingUserImpersonation || hasMissingProfile) && !isButtonLoading ? (
                <Tooltip 
                  positioning="above-start"
                  content={{ className: styles.manageRankTooltip, children: tooltipMessage }} data-testid="policies-manage-rank-tooltip"
                  relationship="label"
                >
                  {button}
                </Tooltip>
              ) : button;
            })()}
            <SearchBox
              className={styles.searchBox}
              placeholder="Enter policy name"
              value={searchQuery}
              onChange={(_, data) => setSearchQuery(data?.value || '')} data-testid="policies-enter-policy-name-search-box" />

          </div>
        </div>

        {/* Content */}
        <div className={styles.content} data-testid="policies-content-div">
          {/* Managed by ADM Section */}
          <div className={styles.section} data-testid="policies-section-div">
            <div className={styles.sectionHeader} data-testid="policies-section-header-div">
              <div className={styles.sectionTitle} data-testid="policies-section-title-div">Managed by ADM</div>
              <div className={styles.sectionDescription} data-testid="policies-section-description-div">
                These Teams External Access policies are managed by ADM. To make changes, edit them here rather than in Teams Admin Center.
              </div>
            </div>

            <div className={styles.tableWrapper} data-testid="policies-table-wrapper-div">
              <Table className={styles.table} data-testid="policies-managed-table">
                <TableHeader className={styles.tableHeader} data-testid="policies-managed-table-header">
                  <TableRow data-testid="policies-managed-header-row">
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-managed-name-table-header-cell">Name</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-managed-assignments-table-header-cell">Assignments</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-managed-disclaimer-table-header-cell">Disclaimer</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-managed-status-table-header-cell">Policy status</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="policies-managed-table-body">
                  {filteredManagedPolicies.length > 0 ?
                    filteredManagedPolicies.map((policy, index) => {
                      const deletionStatus = (policy as any).deletionStatus;
                      const isDeleting = deletionStatus === 'pending' || deletionStatus === 'in_progress';

                      return (
                        <TableRow key={`managed-${index}-${policy.id || policy.name}`} style={{ opacity: isDeleting ? 0.6 : 1 }} data-testid="policies-managed-policy-row">
                          <TableCell className={styles.tableCell} data-testid="policies-managed-name-table-cell">
                            <button
                              type="button"
                              className={isDeleting ? styles.policyDeleting : styles.policyName}
                              onClick={() => !isDeleting && navigate(`/policies/${encodeURIComponent(policy.name)}`)}
                              title={isDeleting ? 'Policy is being deleted...' : `Policy Name: ${policy.name}`}
                              disabled={isDeleting}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                color: 'inherit',
                                textAlign: 'left',
                                cursor: isDeleting ? 'default' : 'pointer',
                                width: '100%',
                                display: 'block'
                              }} data-testid="policies-managed-policy-name-button">

                              {policy.name}
                              {isDeleting && <Spinner size="tiny" style={{ marginLeft: '8px' }} data-testid="policies-deleting-policy-spinner" />}
                            </button>
                            {policy.isTeamsPolicy &&
                              <div className={styles.policySubtext} data-testid="policies-policy-subtext-div">External Access Policy</div>
                            }
                          </TableCell>
                          <TableCell className={styles.tableCell} data-testid="policies-managed-assignments-table-cell">
                            {renderAssignmentSummary(policy, styles)}
                          </TableCell>
                          <TableCell className={styles.tableCell} data-testid="policies-managed-disclaimer-table-cell">
                            {renderDisclaimerCell(policy, disclaimers, disclaimerState, styles)}
                          </TableCell>
                          <TableCell className={styles.tableCell} data-testid="policies-managed-status-table-cell">
                            {(() => {
                              // Show deleting status first
                              if (isDeleting) {
                                return (
                                  <Badge
                                    className={styles.deletingBadge}
                                    appearance="filled" data-testid="policies-managed-status-badge">

                                    {(policy as any).deletionStatus === 'pending' ? 'Queued for Deletion' : 'Deleting...'}
                                  </Badge>);

                              }

                              // For managed policies, show drift status if drift detected
                              if (policy.syncStatus === 'Drift Detected') {
                                return (
                                  <Badge
                                    className={styles.driftBadge}
                                    icon={<AlertRegular data-testid="policies-alert-regular" />}
                                    appearance="filled" data-testid="policies-fix-drift-badge">

                                    Fix drift
                                  </Badge>);

                              }

                              // Managed policies are already onboarded
                              return (
                                <Badge
                                  className={styles.syncBadge}
                                  appearance="filled" data-testid="policies-onboarded-badge">

                                  Onboarded
                                </Badge>);

                            })()}
                          </TableCell>
                        </TableRow>);

                    }) :

                    <TableRow data-testid="policies-managed-empty-row">
                      <TableCell colSpan={4} className={styles.emptyState} data-testid="policies-no-managed-policies-found-table-cell">
                        No managed policies found
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Unmanaged org policies Section */}
          <div className={styles.section} data-testid="policies-section-2-div">
            <div className={styles.sectionHeader} data-testid="policies-section-header-2-div">
              <div className={styles.sectionTitle} data-testid="policies-section-title-2-div">Managed by Teams Admin Center</div>
              <div className={styles.sectionDescription} data-testid="policies-section-description-2-div">
                These Teams External Access policies are managed in Teams Admin Center. To create and manage new policies in ADM, select Add Policy.
              </div>
            </div>

            <div className={styles.tableWrapper} data-testid="policies-table-wrapper-2-div">
              <Table className={styles.table} data-testid="policies-unmanaged-table">
                <TableHeader className={styles.tableHeader} data-testid="policies-unmanaged-table-header">
                  <TableRow data-testid="policies-unmanaged-header-row">
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-unmanaged-name-table-header-cell">Name</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-unmanaged-assignments-table-header-cell">Assignments</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell} data-testid="policies-unmanaged-status-table-header-cell">Status</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="policies-unmanaged-table-body">
                  {filteredUnmanagedPolicies.length > 0 ?
                    filteredUnmanagedPolicies.map((policy, index) =>
                      <TableRow key={`unmanaged-${index}-${policy.id || policy.name}`} data-testid="policies-unmanaged-policy-row">
                        <TableCell className={styles.tableCell} data-testid="policies-unmanaged-name-table-cell">
                          <button
                            type="button"
                            className={styles.policyName}
                            onClick={() => navigate(`/policies/${encodeURIComponent(policy.name)}`)}
                            //adding keyboard accessibility
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/policies/${encodeURIComponent(policy.name)}`);
                              }
                            }}
                            //making the keyboard focusable
                            tabIndex={0}
                            //mouse over effect
                            onMouseOver={() => { }}
                            onFocus={() => { }}
                            onMouseOut={() => { }}
                            onBlur={() => { }}
                            //title attribute for policy name
                            title={`Policy Name: ${policy.name}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              font: 'inherit',
                              color: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                              width: '100%',
                              display: 'block'
                            }} data-testid="policies-policy-name-button">

                            {policy.name}
                          </button>
                          <div className={styles.policySubtext} data-testid="policies-policy-subtext-2-div">External Access Policy</div>
                        </TableCell>
                        <TableCell className={styles.tableCell} data-testid="policies-unmanaged-assignments-table-cell">
                          {renderAssignmentSummary(policy, styles)}
                        </TableCell>
                        <TableCell className={styles.tableCell} data-testid="policies-unmanaged-status-table-cell">
                          {(() => {
                            const federationStatus = getPolicyFederationStatus(policy, managedPolicies);
                            // Determine badge style based on status
                            let badgeClassName = styles.notConfiguredBadge;
                            if (federationStatus.status === 'Onboarded') {
                              badgeClassName = styles.syncBadge; // Green
                            } else if (federationStatus.isReady) {
                              badgeClassName = styles.readyBadge; // Yellow
                            }
                            return (
                              <Badge
                                className={badgeClassName}
                                appearance="filled" data-testid="policies-unmanaged-status-badge">

                                {federationStatus.status}
                              </Badge>);

                          })()}
                        </TableCell>
                      </TableRow>
                    ) :

                    <TableRow data-testid="policies-unmanaged-empty-row">
                      <TableCell colSpan={3} className={styles.emptyState} data-testid="policies-no-unmanaged-policies-found-table-cell">
                        No unmanaged policies found
                      </TableCell>
                    </TableRow>
                  }
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Rank Management Dialog */}
        <RankManagementDialog
          open={rankDialogOpen}
          onClose={() => setRankDialogOpen(false)}
          onSave={handleSaveRanks}
          onSavingChange={setSavingRanks} data-testid="policies-rank-management-dialog" />

      </div>
    </div>
  );
};